import { AttributeType } from '@eppo/js-client-sdk';

import { useViewer } from '@vimeo/viewer';

import { buildSubjectAttributes, getAssignment } from './AssignmentManager';
import BigPictureContextBuilder, { ContextOverrides } from './logging/BigPictureContextBuilder';
import {
  Subject,
  SubjectKeyTypes,
  VariationType,
  VariationTypes,
  VariationValueForType,
} from './types';
import useEppo from './useEppo';

type GetEppoAssignmentProps<T extends VariationType> = {
  flagKey: string;
  subject: Subject;
  bigPictureOverrides?: ContextOverrides;
  defaultAssignment: VariationValueForType<T>;
  assignmentType?: T;
};

type UseEppoAssignmentProps<T extends VariationType> = GetEppoAssignmentProps<T> & {
  disabled?: boolean;
};

const warnedFlags = new Set<string>();

function getEppoAssignment<T extends VariationType>({
  flagKey,
  subject,
  bigPictureOverrides,
  defaultAssignment,
  assignmentType,
}: GetEppoAssignmentProps<T>) {
  if (!assignmentType) {
    if (!warnedFlags.has(flagKey)) {
      console.warn(
        `Eppo flag "${flagKey}" not found in configuration. Check that the flag is enabled in Eppo UI.`,
      );
      warnedFlags.add(flagKey);
    }
    return { assignment: defaultAssignment };
  }

  const builder = new BigPictureContextBuilder(bigPictureOverrides ?? {});
  const bigPictureContexts = builder.getAllContexts();

  const subjectAttributes = buildSubjectAttributes(
    subject.key,
    subject.keyType,
    subject.additionalData,
  );

  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location?.search);
    const eppoNameFromQuery = urlParams.get('eppo_name');
    const eppoVarFromQuery = urlParams.get('eppo_var');

    if (eppoVarFromQuery && eppoNameFromQuery === flagKey) {
      // If eppo_name matches the flagKey and eppo_var is present in the query,
      // return the eppo_var directly as the assignment
      // For development purposes and testing

      if (eppoVarFromQuery && eppoNameFromQuery === flagKey) {
        let forcedVariant: VariationValueForType<T>;
        if (assignmentType === VariationTypes.BOOLEAN) {
          forcedVariant = (eppoVarFromQuery === 'true') as VariationValueForType<T>;
        } else if (
          assignmentType === VariationTypes.NUMERIC ||
          assignmentType === VariationTypes.INTEGER
        ) {
          forcedVariant = Number(eppoVarFromQuery) as VariationValueForType<T>;
        } else if (assignmentType === VariationTypes.JSON) {
          forcedVariant = JSON.parse(eppoVarFromQuery) as VariationValueForType<T>;
        } else {
          forcedVariant = eppoVarFromQuery as VariationValueForType<T>;
        }
        return { assignment: forcedVariant };
      }
    }
  }

  const assignment = getAssignment(
    flagKey,
    subjectAttributes,
    bigPictureContexts,
    defaultAssignment,
    assignmentType,
  ) as VariationValueForType<T>;

  return { assignment: assignment ?? defaultAssignment };
}

export function useEppoAssignment<T extends VariationType>({
  flagKey,
  subject,
  bigPictureOverrides,
  defaultAssignment,
  assignmentType,
  disabled = false,
}: UseEppoAssignmentProps<T>) {
  const viewer = useViewer();
  const { isInitialized: isEppoSDKInitialized } = useEppo({
    flagsConfiguration: viewer?.eppoConfig?.flags,
    bypassInitialization: disabled,
  });
  const subjectTargetingAttributes = useSubjectTargetingAttributes();

  if (disabled || !isEppoSDKInitialized || !viewer?.eppoConfig?.flags) {
    return { assignment: defaultAssignment, isEppoSDKInitialized };
  }

  const { assignment } = getEppoAssignment<T>({
    flagKey,
    subject: {
      ...subject,
      additionalData: {
        ...subjectTargetingAttributes,
        ...subject.additionalData,
      },
    },
    bigPictureOverrides,
    defaultAssignment,
    assignmentType: viewer.eppoConfig.flags[flagKey]?.variationType ?? assignmentType,
  });

  return {
    assignment,
    isEppoSDKInitialized,
  };
}

export function useCuidEppoAssignment<T extends VariationType>({
  additionalData,
  disabled = false,
  ...props
}: Omit<UseEppoAssignmentProps<T>, 'subject'> & {
  additionalData?: Record<string, AttributeType>;
}) {
  const viewer = useViewer();
  return useEppoAssignment({
    ...props,
    subject: {
      key: viewer?.user?.id ?? 0,
      keyType: SubjectKeyTypes.CUID,
      additionalData,
    },
    disabled: !viewer?.user || disabled,
  });
}

export function useVuidEppoAssignment<T extends VariationType>({
  additionalData,
  disabled = false,
  ...props
}: Omit<UseEppoAssignmentProps<T>, 'subject'> & {
  additionalData?: Record<string, AttributeType>;
}) {
  const viewer = useViewer();
  return useEppoAssignment({
    ...props,
    subject: {
      key: viewer?.vuid ?? '',
      keyType: SubjectKeyTypes.VUID,
      additionalData,
    },
    disabled: !viewer?.vuid || disabled,
  });
}

/**
 * Only use this if bucketing needs to be delayed until a user action occurs, e.g. a button click.
 * In most cases, you should use the corresponding `useEppoAssignment` hook instead, which will automatically
 * fetch the assignment when the component mounts.
 */
export function useGetEppoAssignment() {
  const viewer = useViewer();
  const { isInitialized: isEppoSDKInitialized } = useEppo({
    flagsConfiguration: viewer?.eppoConfig?.flags,
  });

  const subjectTargetingAttributes = useSubjectTargetingAttributes();
  function getEppoAssignmentWithDefaults<T extends VariationType>(
    props: GetEppoAssignmentProps<T>,
  ) {
    if (!isEppoSDKInitialized || !viewer?.eppoConfig?.flags) {
      return { assignment: props.defaultAssignment };
    }

    return getEppoAssignment<T>({
      ...props,
      subject: {
        ...props.subject,
        additionalData: {
          ...subjectTargetingAttributes,
          ...props.subject.additionalData,
        },
      },
      assignmentType: viewer.eppoConfig.flags[props.flagKey]?.variationType ?? props.assignmentType,
    });
  }

  return { getEppoAssignment: getEppoAssignmentWithDefaults, isEppoSDKInitialized };
}

export function useGetCuidEppoAssignment() {
  const viewer = useViewer();
  const { isInitialized: isEppoSDKInitialized } = useEppo({
    flagsConfiguration: viewer?.eppoConfig?.flags,
  });

  const subjectTargetingAttributes = useSubjectTargetingAttributes();
  function getEppoAssignmentWithDefaults<T extends VariationType>(
    props: GetEppoAssignmentProps<T>,
  ) {
    if (!isEppoSDKInitialized || !viewer?.eppoConfig?.flags || !viewer?.user?.id) {
      return { assignment: props.defaultAssignment };
    }

    return getEppoAssignment<T>({
      ...props,
      subject: {
        key: viewer.user.id,
        keyType: SubjectKeyTypes.CUID,
        additionalData: {
          ...subjectTargetingAttributes,
          ...props.subject.additionalData,
        },
      },
      assignmentType: viewer.eppoConfig.flags[props.flagKey]?.variationType ?? props.assignmentType,
    });
  }

  return { getEppoAssignment: getEppoAssignmentWithDefaults, isEppoSDKInitialized };
}

export function useGetVuidEppoAssignment() {
  const viewer = useViewer();
  const { isInitialized: isEppoSDKInitialized } = useEppo({
    flagsConfiguration: viewer?.eppoConfig?.flags,
  });

  const subjectTargetingAttributes = useSubjectTargetingAttributes();
  function getEppoAssignmentWithDefaults<T extends VariationType>(
    props: GetEppoAssignmentProps<T>,
  ) {
    if (!isEppoSDKInitialized || !viewer?.eppoConfig?.flags || !viewer?.vuid) {
      return { assignment: props.defaultAssignment };
    }

    return getEppoAssignment<T>({
      ...props,
      subject: {
        key: viewer?.vuid ?? '',
        keyType: SubjectKeyTypes.VUID,
        additionalData: {
          ...subjectTargetingAttributes,
          ...props.subject.additionalData,
        },
      },
      assignmentType: viewer.eppoConfig.flags[props.flagKey]?.variationType ?? props.assignmentType,
    });
  }

  return { getEppoAssignment: getEppoAssignmentWithDefaults, isEppoSDKInitialized };
}

function useSubjectTargetingAttributes() {
  const viewer = useViewer();
  if (!viewer) return {};

  return {
    cuid: viewer.user?.id,
    vuid: viewer.vuid,
    is_free: !viewer.user?.capabilities?.hasPaid,
    is_enterprise: viewer.user?.capabilities?.hasEnterprise,
    is_staff: viewer.user?.badge?.type === 'staff',
    device_type: getDeviceType(),
    language: viewer.locale,
    location: viewer.location,
  } as Record<string, AttributeType>;
}

const getDeviceType = (): 'tablet' | 'mobile' | 'desktop' | undefined => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return undefined;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(navigator.userAgent)) return 'tablet';
  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      navigator.userAgent,
    )
  )
    return 'mobile';
  return 'desktop';
};
